import React from "react"
import { motion } from "framer-motion"
import { Plane, CheckCircle, Globe, Shield, ClipboardList, Send, FileText, Users } from "lucide-react"
import Layout from "../../components/common/Layout"
import SEO from "../../components/common/SEO"
import "./ServicePage.css"

const VisaAssistance = () => {
  const visaFeatures = [
    {
      icon: <Globe size={24} />,
      title: "Visa Consultation",
      desc: "Personalized one-on-one consultations to evaluate your profile and recommend the most suitable visa type."
    },
    {
      icon: <ClipboardList size={24} />,
      title: "Customized Checklists",
      desc: "Detailed, visa-specific document checklists tailored to your unique situation—eliminating guesswork."
    },
    {
      icon: <FileText size={24} />,
      title: "Application Drafting",
      desc: "Professional assistance in accurately completing visa forms and drafting strong SOPs or cover letters."
    },
    {
      icon: <Plane size={24} />,
      title: "Departure Briefing",
      desc: "From travel and accommodation to airport formalities — we equip you with everything for a seamless journey."
    }
  ]

  const visaTypes = [
    { title: "Student Visa", desc: "Expert guidance on choosing the right course and handling every step of your student visa process." },
    { title: "Tourist/Visitor Visa", desc: "Hassle-free tourist visa assistance for stress-free international travel plans." },
    { title: "Dependent Visa", desc: "Trusted services to reunite you with loved ones abroad, guiding you through all requirements." },
    { title: "Business Visa", desc: "Efficient business visa processing to help you explore, invest, and grow your global presence." }
  ]

  return (
    <Layout>
      <SEO
        title="Visa Assistance - Expert Visa Services | Welsh Education Group"
        description="Professional visa assistance services for international students. Get expert guidance for your student visa application."
        url="/services/visa"
      />

      <div className="service-page">
        <section className="service-hero-modern">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="hero-icon-large"
            >
              <Plane size={40} />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Visa Assistance
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              Streamlining your path to global opportunities with expert-led visa solutions and 24/7 support.
            </motion.p>
          </div>
        </section>

        <section className="service-content-modern">
          <div className="container">
            <div className="service-grid-modern">
              <div className="main-service-info">
                <h2>Our Expert Process</h2>
                <p>
                  Our experienced visa consultants offer end-to-end support to simplify your visa journey. From initial consultation to final approval, we ensure your application is accurate, complete, and positioned for success. We have helped thousands of students achieve their dreams with a 98% success rate.
                </p>

                <div className="service-features-list">
                  {visaFeatures.map((feature, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="feature-pill"
                    >
                      <div className="feature-pill-icon">{feature.icon}</div>
                      <div className="feature-pill-content">
                        <h4>{feature.title}</h4>
                        <p>{feature.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="visa-types-section" style={{ marginTop: '64px' }}>
                  <h2 style={{ marginBottom: '32px' }}>Visa Categories</h2>
                  <div className="info-grid">
                    {visaTypes.map((type, idx) => (
                      <div key={idx} className="info-card">
                        <h3>{type.title}</h3>
                        <p style={{ color: 'var(--muted-foreground)' }}>{type.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="service-sidebar-modern">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  className="sidebar-cta-card"
                >
                  <Shield className="text-gradient" size={48} style={{ margin: '0 auto 24px' }} />
                  <h3>Start Your Process</h3>
                  <p>Begin your consultation today with our senior experts.</p>
                  <button className="sidebar-btn highlight-glow">Book Free Consultation</button>
                </motion.div>

                <div className="info-card">
                  <h3 style={{ fontSize: '18px', marginBottom: '20px' }}>Quick Stats</h3>
                  <ul className="stats-list" style={{ listStyle: 'none', padding: 0 }}>
                    <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <CheckCircle size={16} color="var(--cyan-glow)" /> <span>98% Success Rate</span>
                    </li>
                    <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <CheckCircle size={16} color="var(--cyan-glow)" /> <span>15+ Years Experience</span>
                    </li>
                    <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <CheckCircle size={16} color="var(--cyan-glow)" /> <span>50+ Countries Covered</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  )
}

export default VisaAssistance
